import { InspectionTemplateColumn } from "@/shared/models/Inspection";
import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	InspectionRow,
	InspectionRowDefect,
	InspectionValue,
} from "../models/Inspection";
import {
	EMPTY_INSPECTION_DASHBOARD_TOTALS,
	InspectionDashboard,
	InspectionDashboardBucket,
	InspectionDashboardBucketKey,
	InspectionDashboardDefectTrendPoint,
	InspectionDashboardGrain,
	InspectionDashboardTotals,
} from "../models/InspectionDashboard";
import {
	InspectionReport,
	InspectionReportFilterOptions,
	InspectionReportRow,
	InspectionReportSheetDetail,
	ProductionEventOption,
} from "../models/InspectionReport";

const moment = getMomentTz();

export const getInspectionReport = async (
	params: {
		startDate: Date | null;
		endDate: Date | null;
		sectionId?: number | null;
		eventId?: number | null;
		heatId?: number | null;
		surfaceDefectIds?: number[];
		problemsOnly?: boolean;
	},
	signal?: AbortSignal,
): Promise<InspectionReport> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "StartDate", value: params.startDate?.toISOString() ?? null },
		{ name: "EndDate", value: params.endDate?.toISOString() ?? null },
		{ name: "SectionId", value: params.sectionId ?? null },
		{ name: "EventId", value: params.eventId ?? null },
		{ name: "HeatId", value: params.heatId ?? null },
		{
			name: "SurfaceDefectIds",
			value: params.surfaceDefectIds?.length
				? params.surfaceDefectIds.join(",")
				: null,
		},
		{ name: "ProblemsOnly", value: params.problemsOnly ?? false },
	];

	const resp = await apiService.callV2(
		"[M1].[GetInspectionReport]",
		parameters,
		signal,
	);

	if (!resp.ok)
		throw new Error(resp.message || "Error fetching the inspections report");

	const tables = get(resp, "data.tables", {});

	const sheets = (get(tables, "0.rows", []) as InspectionReportRow[]).map(
		(row) => ({
			...row,
			ProductionDate: row.ProductionDate
				? moment.utc(row.ProductionDate).toDate()
				: null,
			FirstReadingAt: row.FirstReadingAt
				? moment.utc(row.FirstReadingAt).toDate()
				: null,
			LastReadingAt: row.LastReadingAt
				? moment.utc(row.LastReadingAt).toDate()
				: null,
			EventStart: row.EventStart ? moment.utc(row.EventStart).toDate() : null,
		}),
	);

	const columnRows = get(tables, "1.rows", []) as InspectionTemplateColumn[];
	const readingRows = get(tables, "2.rows", []) as InspectionRow[];
	const valueRows = get(tables, "3.rows", []) as InspectionValue[];
	const defectRows = get(tables, "4.rows", []) as InspectionRowDefect[];

	const columnsByTemplate = new Map<number, InspectionTemplateColumn[]>();
	for (const column of columnRows) {
		const templateId = column.TemplateId as number;
		const columns = columnsByTemplate.get(templateId) ?? [];

		columns.push(column);
		columnsByTemplate.set(templateId, columns);
	}

	const valuesByRow = new Map<number, Record<number, InspectionValue>>();
	for (const value of valueRows) {
		const cells = valuesByRow.get(value.RowId) ?? {};

		cells[value.ColumnId] = {
			...value,
			DateTimeValue: value.DateTimeValue
				? moment.utc(value.DateTimeValue).toDate()
				: null,
			UpdatedAt: value.UpdatedAt ? moment.utc(value.UpdatedAt).toDate() : null,
		};
		valuesByRow.set(value.RowId, cells);
	}

	const defectsByRow = new Map<number, InspectionRowDefect[]>();
	for (const defect of defectRows) {
		const defects = defectsByRow.get(defect.RowId) ?? [];

		defects.push(defect);
		defectsByRow.set(defect.RowId, defects);
	}

	const details = new Map<number, InspectionReportSheetDetail>();
	for (const sheet of sheets) {
		details.set(sheet.InspectionId, {
			columns: columnsByTemplate.get(sheet.TemplateId) ?? [],
			readings: [],
		});
	}

	for (const row of readingRows) {
		details.get(row.InspectionId)?.readings.push({
			...row,
			RowTimestamp: row.RowTimestamp
				? moment.utc(row.RowTimestamp).toDate()
				: null,
			CreationTime: row.CreationTime
				? moment.utc(row.CreationTime).toDate()
				: null,
			UpdatedAt: row.UpdatedAt ? moment.utc(row.UpdatedAt).toDate() : null,
			ValueByColumnId: valuesByRow.get(row.RowId) ?? {},
			Defects: defectsByRow.get(row.RowId) ?? [],
		});
	}

	return {
		sheets,
		details,
	};
};

export const getInspectionReportFilters = async (
	params: {
		startDate: Date | null;
		endDate: Date | null;
		sectionId?: number | null;
	},
	signal?: AbortSignal,
): Promise<InspectionReportFilterOptions> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "StartDate", value: params.startDate?.toISOString() ?? null },
		{ name: "EndDate", value: params.endDate?.toISOString() ?? null },
		{ name: "SectionId", value: params.sectionId ?? null },
	];

	const resp = await apiService.callV2(
		"[M1].[GetInspectionReportFilters]",
		parameters,
		signal,
	);

	if (!resp.ok)
		throw new Error(resp.message || "Error fetching the report filters");

	const tables = get(resp, "data.tables", {});

	return {
		events: (get(tables, "0.rows", []) as ProductionEventOption[]).map(
			(row) => ({
				...row,
				Start: row.Start ? moment.utc(row.Start).toDate() : null,
				End: row.End ? moment.utc(row.End).toDate() : null,
			}),
		),
		heats: get(tables, "1.rows", []),
	};
};

export const getInspectionDashboard = async (
	params: {
		startDate: Date | null;
		endDate: Date | null;
		eventId?: number | null;
	},
	signal?: AbortSignal,
): Promise<InspectionDashboard> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "StartDate", value: params.startDate?.toISOString() ?? null },
		{ name: "EndDate", value: params.endDate?.toISOString() ?? null },
		{ name: "EventId", value: params.eventId ?? null },
	];

	const resp = await apiService.callV2(
		"[M1].[GetInspectionDashboard]",
		parameters,
		signal,
	);

	if (!resp.ok)
		throw new Error(resp.message || "Error fetching the inspections dashboard");

	const tables = get(resp, "data.tables", {});
	const totals = get(tables, "0.rows.0", null) as
		| (InspectionDashboardTotals & { BucketGrain: InspectionDashboardGrain })
		| null;

	return {
		grain: totals?.BucketGrain ?? "day",
		totals: totals ?? EMPTY_INSPECTION_DASHBOARD_TOTALS,
		buckets: (get(tables, "1.rows", []) as InspectionDashboardBucket[]).map(
			(row) => ({ ...row, ...toBucketKey(row) }),
		),
		defects: get(tables, "2.rows", []),
		sections: get(tables, "3.rows", []),
		heats: get(tables, "4.rows", []),
		defectTrend: (
			get(tables, "5.rows", []) as InspectionDashboardDefectTrendPoint[]
		).map((row) => ({ ...row, ...toBucketKey(row) })),
	};
};

const toBucketKey = (row: InspectionDashboardBucketKey) => ({
	BucketStartUtc: moment.utc(row.BucketStartUtc).toDate(),
	ProductionDate: row.ProductionDate
		? moment.utc(row.ProductionDate).toDate()
		: null,
	Shift: row.Shift ?? null,
});

export const getInspectionDashboardPdf = async (
	request: {
		startDate: string | null;
		endDate: string | null;
		eventId: number | null;
		periodLabel: string;
	},
	signal?: AbortSignal,
): Promise<Blob> => {
	const response = await fetch("/api/inspection-dashboards/pdf", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(request),
		signal,
	});

	if (!response.ok) {
		const message = await response.text();

		throw new Error(
			message || "Error generating the inspections dashboard PDF",
		);
	}

	return response.blob();
};

export const getInspectionReportPdf = async (
	request: {
		startDate: string | null;
		endDate: string | null;
		sectionId: number | null;
		eventId: number | null;
		heatId: number | null;
		surfaceDefectIds: number[];
		problemsOnly: boolean;
		sectionName: string | null;
		heatName: string | null;
		surfaceDefectNames: string[];
	},
	signal?: AbortSignal,
): Promise<Blob> => {
	const response = await fetch("/api/inspection-reports/pdf", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(request),
		signal,
	});

	if (!response.ok) {
		const message = await response.text();

		throw new Error(message || "Error generating the inspections report PDF");
	}

	return response.blob();
};
